import { create } from 'zustand';
import type { DateInvite } from '@/api/types';

/**
 * When to offer push and "Add to home screen" (SPEC_V9 §D3): only after a
 * meaningful moment — a scheduled date (push), or after the first date (push
 * and install) — and never on first load. "Not now" snoozes for a week.
 * Everything here is per device (localStorage), and reading it never throws.
 */

export const SNOOZE_MS = 7 * 24 * 3600_000;
const KEYS = { push: 'popin.nudge.push', install: 'popin.nudge.install', firstDate: 'popin.firstDateDone' } as const;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, v: string) {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* private mode: the nudge just comes back next time */
  }
}

/** "done" (answered) or a snooze timestamp; null = never asked. */
export function snoozed(value: string | null, now = Date.now()): boolean {
  if (!value) return false;
  if (value === 'done') return true;
  const t = Number(value);
  return Number.isFinite(t) && now - t < SNOOZE_MS;
}

/** A scheduled date with a time (the moment push is worth it). */
export function hasScheduledDate(list: DateInvite[] | undefined): boolean {
  return !!list?.some((i) => (i.status === 'scheduled' || (i.status === 'accepted' && !!i.starts_at)) && !i.session_id);
}

/** A finished invite date counts as "after the first date". */
export function hadDate(list: DateInvite[] | undefined): boolean {
  return !!list?.some((i) => i.status === 'completed');
}

/** Never on these screens: the landing page, onboarding, a call. */
export function quietPath(path: string): boolean {
  return path === '/' || path.startsWith('/call') || path.startsWith('/onboarding') || path.startsWith('/login') || path.startsWith('/d/');
}

export interface PushAskInput {
  available: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  scheduled: boolean;
  firstDateDone: boolean;
  path: string;
  asked: string | null;
  now?: number;
}

export function shouldAskPush(i: PushAskInput): boolean {
  return (
    i.available &&
    i.permission === 'default' &&
    !i.subscribed &&
    (i.scheduled || i.firstDateDone) &&
    !quietPath(i.path) &&
    !snoozed(i.asked, i.now)
  );
}

export interface InstallInput {
  canPrompt: boolean;
  installed: boolean;
  firstDateDone: boolean;
  path: string;
  asked: string | null;
  now?: number;
}

export function shouldOfferInstall(i: InstallInput): boolean {
  return i.canPrompt && !i.installed && i.firstDateDone && !quietPath(i.path) && !snoozed(i.asked, i.now);
}

/** The deferred `beforeinstallprompt` event (Chromium only). */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NudgeState {
  installEvent: InstallPromptEvent | null;
  installed: boolean;
  firstDateDone: boolean;
  pushAsked: string | null;
  installAsked: string | null;
  setInstallEvent: (e: InstallPromptEvent | null) => void;
  markInstalled: () => void;
  markFirstDate: () => void;
  answerPush: (how: 'done' | 'later') => void;
  answerInstall: (how: 'done' | 'later') => void;
}

function standalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

export const useNudges = create<NudgeState>((set) => ({
  installEvent: null,
  installed: typeof window !== 'undefined' && standalone(),
  firstDateDone: read(KEYS.firstDate) === '1',
  pushAsked: read(KEYS.push),
  installAsked: read(KEYS.install),
  setInstallEvent: (installEvent) => set({ installEvent }),
  markInstalled: () => set({ installed: true, installEvent: null }),
  markFirstDate: () => {
    write(KEYS.firstDate, '1');
    set({ firstDateDone: true });
  },
  answerPush: (how) => {
    const v = how === 'done' ? 'done' : String(Date.now());
    write(KEYS.push, v);
    set({ pushAsked: v });
  },
  answerInstall: (how) => {
    const v = how === 'done' ? 'done' : String(Date.now());
    write(KEYS.install, v);
    set({ installAsked: v });
  },
}));

/** Shows the browser's install dialog; true when the person accepted. */
export async function promptInstall(): Promise<boolean> {
  const ev = useNudges.getState().installEvent;
  if (!ev) return false;
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    useNudges.getState().setInstallEvent(null);
    if (choice.outcome === 'accepted') useNudges.getState().markInstalled();
    return choice.outcome === 'accepted';
  } catch {
    return false;
  }
}
